import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Database, Layers, Code, Calendar, Hash, Type, ToggleLeft, FileText, Share2 } from 'lucide-react';

const TYPE_ICONS = {
  string: Type, code: Code, date: Calendar, number: Hash, integer: Hash,
  float: Hash, boolean: ToggleLeft, text: FileText,
};
const TYPE_COLORS = {
  string: 'bg-zinc-100 text-zinc-600', code: 'bg-blue-50 text-blue-700',
  date: 'bg-amber-50 text-amber-700', number: 'bg-emerald-50 text-emerald-700',
  integer: 'bg-emerald-50 text-emerald-700', float: 'bg-teal-50 text-teal-700',
  boolean: 'bg-purple-50 text-purple-700', text: 'bg-rose-50 text-rose-600',
};

export default function PublicProposalPage() {
  const { shareToken } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/proposals/share/${shareToken}`)
      .then(r => setProposal(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Proposal not found'))
      .finally(() => setLoading(false));
  }, [shareToken]);

  if (loading) {
    return (<div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div>);
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center" data-testid="proposal-error">
        <div className="text-center">
          <Share2 className="w-10 h-10 text-[#9CA3AF] mx-auto mb-3" />
          <h1 className="text-xl font-bold text-[#0A0A0A] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Proposal Not Available</h1>
          <p className="text-sm text-[#4B5563]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="public-proposal-page">
      {/* Header bar */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center gap-3">
          <img src="/triomics-logo.png" alt="Triomics" className="h-5" />
          <span className="text-sm font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>Triomics Data Dictionary</span>
          <span className="text-xs text-[#9CA3AF] ml-2">Shared Proposal</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Proposal header */}
        <div className="bg-white border border-[#E5E7EB] p-6 mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {proposal.title}
          </h1>
          {proposal.description && <p className="text-sm text-[#4B5563] mb-4">{proposal.description}</p>}
          <div className="flex items-center gap-4 text-xs text-[#9CA3AF]">
            <div className="flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-[#002FA7]" strokeWidth={1.5} />
              <span className="text-[#4B5563] font-medium">{proposal.schema_name}</span>
            </div>
            <span>{proposal.attribute_ids?.length || 0} attributes</span>
            {proposal.created_at && <span>Created {new Date(proposal.created_at).toLocaleDateString()}</span>}
          </div>
        </div>

        {/* Entity tree */}
        {(proposal.entities || []).map(entity => (
          <div key={entity.entity_id} className="bg-white border border-[#E5E7EB] mb-px" data-testid={`public-entity-${entity.entity_id}`}>
            <div className="flex items-center gap-3 px-4 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <Layers className="w-4 h-4 text-[#002FA7]" strokeWidth={1.5} />
              <span className="text-sm font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>{entity.entity_name}</span>
              {entity.is_repeating && (
                <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-700 font-medium">REPEATING</span>
              )}
              <span className="text-xs text-[#9CA3AF] ml-auto">{entity.attributes.length} attributes</span>
            </div>
            <div>
              {entity.attributes.map(attr => {
                const Icon = TYPE_ICONS[attr.data_type] || Type;
                const colorClass = TYPE_COLORS[attr.data_type] || 'bg-zinc-100 text-zinc-600';
                return (
                  <div key={attr.attribute_id} className="flex items-start gap-3 px-4 py-3 border-b border-[#E5E7EB] last:border-b-0">
                    <Icon className="w-3.5 h-3.5 text-[#9CA3AF] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium text-[#0A0A0A]">{attr.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 font-medium ${colorClass}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{attr.data_type}</span>
                        {attr.is_required && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 font-medium">REQ</span>}
                        {attr.group_name && <span className="text-[10px] text-[#9CA3AF]">{attr.group_name}</span>}
                      </div>
                      {attr.description && <p className="text-xs text-[#4B5563] mb-1">{attr.description}</p>}
                      {attr.valueset_name && (
                        <div className="mt-1">
                          <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.1em]">Valueset: {attr.valueset_name}</span>
                          {attr.valueset_codes?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {attr.valueset_codes.map(c => (
                                <span key={c.code} className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-700" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                                  {c.code}: {c.label}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {(!proposal.entities || proposal.entities.length === 0) && (
          <div className="bg-white border border-dashed border-[#E5E7EB] p-12 text-center text-sm text-[#9CA3AF]">
            No attributes in this proposal.
          </div>
        )}

        <div className="mt-8 text-center text-xs text-[#9CA3AF]">
          <Database className="w-4 h-4 inline mr-1" strokeWidth={1.5} />
          Powered by Triomics Data Dictionary
        </div>
      </div>
    </div>
  );
}
